import React, { useEffect } from 'react';
import { getPostBySlug, getRecentPosts } from './blogData';

const BlogPost = ({ slug, onBack, onSelectPost }) => {
  const post = getPostBySlug(slug);
  const recentPosts = getRecentPosts(3).filter(p => p.slug !== slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Post Not Found</h1>
          <button 
            onClick={onBack}
            className="text-blue-600 hover:text-blue-800"
          >
            Return to Blog
          </button>
        </div>
      </div>
    );
  }

  // Convert markdown-style content to HTML
  const renderContent = (content) => {
    return content
      .split('\n')
      .map((line, index) => {
        // Headers
        if (line.startsWith('## ')) {
          return <h2 key={index} className="text-2xl font-bold text-gray-900 mt-8 mb-4">{line.replace('## ', '')}</h2>;
        }
        if (line.startsWith('### ')) {
          return <h3 key={index} className="text-xl font-semibold text-gray-800 mt-6 mb-3">{line.replace('### ', '')}</h3>;
        }
        // Bold items (list style)
        if (line.startsWith('**') && line.includes(':**')) {
          const [label, value] = line.split(':**');
          return (
            <p key={index} className="mb-2">
              <strong className="text-gray-900">{label.replace('**', '')}:</strong>
              {value?.replace('**', '')}
            </p>
          );
        }
        // List items
        if (line.startsWith('- ')) {
          return <li key={index} className="ml-6 mb-2 text-gray-700">{line.replace('- ', '')}</li>;
        }
        if (line.startsWith('✅ ') || line.startsWith('❌ ')) {
          return <li key={index} className="ml-6 mb-2 text-gray-700 list-none">{line}</li>;
        }
        // Numbered list
        if (/^\d+\.\s/.test(line)) {
          return <li key={index} className="ml-6 mb-2 text-gray-700 list-decimal">{line.replace(/^\d+\.\s/, '')}</li>;
        }
        // Table (simplified - just show as text)
        if (line.startsWith('|')) {
          return <p key={index} className="font-mono text-sm bg-gray-100 px-2 py-1 mb-1">{line}</p>;
        }
        // Empty line
        if (line.trim() === '') {
          return <br key={index} />;
        }
        // Regular paragraph
        return <p key={index} className="text-gray-700 mb-4 leading-relaxed">{line}</p>;
      });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={onBack}
              className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Blog
            </button>
            <span className="text-gray-500 text-sm">{post.readTime}</span>
          </div>
        </div>
      </header>

      {/* Article */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Meta */}
        <div className="flex items-center gap-4 mb-6">
          <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
            {post.category}
          </span>
          <span className="text-gray-500 text-sm">
            {new Date(post.publishDate).toLocaleDateString('en-US', { 
              month: 'long', 
              day: 'numeric', 
              year: 'numeric' 
            })}
          </span>
        </div>

        {/* Title */}
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
          {post.title}
        </h1>

        {/* Excerpt */}
        <p className="text-xl text-gray-600 mb-8 leading-relaxed">
          {post.excerpt}
        </p>

        {/* Author */}
        <div className="flex items-center gap-4 pb-8 border-b border-gray-200">
          <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">
            AE
          </div>
          <div>
            <p className="font-medium text-gray-900">{post.author}</p>
            <p className="text-gray-500 text-sm">AdElevate Marketing Team</p>
          </div>
        </div>

        {/* Content */}
        <div className="prose prose-lg max-w-none mt-8">
          {renderContent(post.content)}
        </div>

        {/* Tags */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-500 mb-3">Tags</h4>
          <div className="flex flex-wrap gap-2">
            {post.tags.map(tag => (
              <span 
                key={tag}
                className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Ready to Create AI-Powered Ads?</h3>
          <p className="text-blue-100 mb-6">
            Start creating professional text, image, and video ads in seconds.
          </p>
          <button 
            onClick={() => window.location.href = '/'}
            className="px-8 py-3 bg-white text-blue-600 font-bold rounded-lg hover:bg-gray-100 transition"
          >
            Try AdElevate Free
          </button>
        </div>
      </article>

      {/* Related Posts */}
      {recentPosts.length > 0 && (
        <div className="bg-gray-50 py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">More Articles</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {recentPosts.slice(0, 2).map(relatedPost => (
                <article 
                  key={relatedPost.id}
                  onClick={() => onSelectPost(relatedPost.slug)}
                  className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition cursor-pointer"
                >
                  <div className="p-6">
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                      {relatedPost.category}
                    </span>
                    <h3 className="text-lg font-bold text-gray-900 mt-3 mb-2 hover:text-blue-600">
                      {relatedPost.title}
                    </h3>
                    <p className="text-gray-600 text-sm line-clamp-2">
                      {relatedPost.excerpt}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p>&copy; 2026 AdElevate by 3STech. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default BlogPost;
